import ErrorHandler from '../utils/errorHandler';
import Logger from '../utils/logger';

export class AuthService {
  constructor(baseService) {
    this.base = baseService;
  }

  async processSession(sessionId) {
    // Re-check IPC availability
    this.base.checkIPCAvailability();
    const hasIPC = this.base.useIPC && window.electronAPI?.backend?.authSession;
    if (hasIPC) {
      try {
        const result = await window.electronAPI.backend.authSession(sessionId);
        if (result.session_token) {
          await this.base.setSessionCookie(result.session_token, { maxAgeSeconds: 86400 });
        }
        return result;
      } catch (error) {
        const handled = ErrorHandler.handle(error, {
          component: 'ApiService',
          action: 'processSession',
          endpoint: '/auth/session',
          metadata: { method: 'IPC' }
        });
        Logger.warn('IPC processSession failed, falling back to HTTP', { handled, component: 'ApiService' });
      }
    }
    const response = await this.base.request('/auth/session', {
      method: 'POST',
      body: { session_id: sessionId },
    });
    await this.base.setSessionCookie(response.session_token, { maxAgeSeconds: 86400 });
    return response;
  }

  async googleAuth(idToken) {
    this.base.checkIPCAvailability();
    const hasIPC = this.base.useIPC && window.electronAPI?.backend?.authGoogle;
    if (hasIPC) {
      try {
        const result = await window.electronAPI.backend.authGoogle(idToken);
        if (result.session_token) {
          await this.base.setSessionCookie(result.session_token);
        }
        return result;
      } catch (error) {
        const handled = ErrorHandler.handle(error, {
          component: 'ApiService',
          action: 'googleAuth',
          endpoint: '/auth/google',
          metadata: { method: 'IPC' }
        });
        Logger.warn('IPC googleAuth failed, falling back to HTTP', { handled, component: 'ApiService' });
      }
    }
    const response = await this.base.request('/auth/google', {
      method: 'POST',
      body: { id_token: idToken },
    });
    this.base.setSessionCookie(response.session_token);
    return response;
  }

  async emailLogin(email, password) {
    // Re-check IPC availability
    this.base.checkIPCAvailability();
    const hasIPC = this.base.useIPC && window.electronAPI?.backend?.authLogin;
    
    if (hasIPC) {
      try {
        Logger.info('Using IPC for emailLogin');
        const result = await window.electronAPI.backend.authLogin(email, password);
        // Save session token
        if (result.session_token) {
          await this.base.setSessionCookie(result.session_token);
        }
        return result;
      } catch (error) {
        const handled = ErrorHandler.handle(error, {
          component: 'ApiService',
          action: 'emailLogin',
          endpoint: '/auth/email/login',
          metadata: { method: 'IPC' }
        });
        if (process.env.NODE_ENV === 'development') {
          Logger.warn('IPC emailLogin failed, falling back to HTTP', { handled });
        }
        // Fall through to HTTP
      }
    }
    
    // HTTP fallback
    const response = await this.base.request('/auth/email/login', {
      method: 'POST',
      body: { email, password },
    });
    
    await this.base.setSessionCookie(response.session_token);
    
    return response;
  }

  async emailRegister(name, email, password, couponCode) {
    // Re-check IPC availability
    this.base.checkIPCAvailability();
    const hasIPC = this.base.useIPC && window.electronAPI?.backend?.authRegister;

    if (hasIPC) {
      try {
        const payload = { name, email, password };
        if (couponCode) payload.coupon_code = couponCode;
        const result = await window.electronAPI.backend.authRegister(payload);
        // Save session token
        if (result.session_token) {
          await this.base.setSessionCookie(result.session_token);
        }
        return result;
      } catch (error) {
        const handled = ErrorHandler.handle(error, {
          component: 'ApiService',
          action: 'emailRegister',
          endpoint: '/auth/email/register',
          metadata: { method: 'IPC' }
        });
        Logger.warn('IPC emailRegister failed, falling back to HTTP', { handled, component: 'ApiService' });
        // Fall through to HTTP
      }
    }

    // HTTP fallback
    const body = { name, email, password };
    if (couponCode) body.coupon_code = couponCode;
    const response = await this.base.request('/auth/email/register', {
      method: 'POST',
      body,
    });

    await this.base.setSessionCookie(response.session_token);

    return response;
  }

  async getCurrentUser() {
    // Re-check IPC availability - force re-check
    this.base.checkIPCAvailability();
    
    // Direct check for electronAPI (more reliable than getters)
    const hasElectronAPI = typeof window !== 'undefined' && window.electronAPI?.backend?.authMe;
    
    if (hasElectronAPI) {
      try {
        const sessionToken = await this.base.getSessionToken();
        if (!sessionToken) {
          Logger.debug('No session token found in secure storage', { component: 'ApiService' });
          return null;
        } else {
          const user = await window.electronAPI.backend.authMe(sessionToken);
          Logger.debug('IPC getCurrentUser succeeded', { userEmail: user?.email, component: 'ApiService' });
          return user;
        }
      } catch (error) {
        const handled = ErrorHandler.handle(error, {
          component: 'ApiService',
          action: 'getCurrentUser',
          endpoint: '/auth/me',
          metadata: { method: 'IPC' }
        });
        const isUnauthorized = handled?.code === 'HTTP_401' || /unauthorized|authentication/i.test(handled?.message || '');
        if (isUnauthorized) {
          await this.base.setSessionCookie(null);
          return null;
        }
        Logger.warn('IPC getCurrentUser failed', { handled, component: 'ApiService' });
        return null;
      }
    } else {
      Logger.debug('electronAPI.backend.authMe not available', {
        electronAPIExists: typeof window !== 'undefined' && !!window.electronAPI,
        backendExists: typeof window !== 'undefined' && !!window.electronAPI?.backend,
        authMeExists: typeof window !== 'undefined' && !!window.electronAPI?.backend?.authMe,
        component: 'ApiService'
      });
    }
    
    // HTTP fallback
    return this.base.request('/auth/me');
  }

  async logout() {
    // Re-check IPC availability
    this.base.checkIPCAvailability();
    const hasIPC = this.base.useIPC && window.electronAPI?.backend?.authLogout;
    
    if (hasIPC) {
      try {
        const sessionToken = await this.base.getSessionToken();
        await window.electronAPI.backend.authLogout(sessionToken);
        await this.base.setSessionCookie(null);
        return { success: true };
      } catch (error) {
        const handled = ErrorHandler.handle(error, {
          component: 'ApiService',
          action: 'logout',
          endpoint: '/auth/logout',
          metadata: { method: 'IPC' }
        });
        Logger.warn('IPC logout failed, falling back to HTTP', { handled, component: 'ApiService' });
        // Fall through to HTTP
      }
    }
    
    // HTTP fallback
    const result = await this.base.request('/auth/logout', { method: 'POST' });
    await this.base.setSessionCookie(null);
    return result;
  }

  async updateProfile(updates) {
    // Re-check IPC availability
    this.base.checkIPCAvailability();
    const hasIPC = this.base.useIPC && window.electronAPI?.backend?.authProfile;
    
    if (hasIPC) {
      try {
        const sessionToken = await this.base.getSessionToken();
        return await window.electronAPI.backend.authProfile(sessionToken, updates);
      } catch (error) {
        const handled = ErrorHandler.handle(error, {
          component: 'ApiService',
          action: 'updateProfile',
          endpoint: '/auth/profile',
          metadata: { method: 'IPC' }
        });
        Logger.warn('IPC updateProfile failed, falling back to HTTP', { handled, component: 'ApiService' });
        // Fall through to HTTP
      }
    }
    
    // HTTP fallback
    return this.base.request('/auth/profile', {
      method: 'PUT',
      body: updates,
    });
  }

  async forgotPassword(email) {
    return this.base.request('/auth/forgot-password', {
      method: 'POST',
      body: { email },
    });
  }

  async resetPassword(token, password) {
    return this.base.request('/auth/reset-password', {
      method: 'POST',
      body: { token, password },
    });
  }

  async verifyEmail(token) {
    return this.base.request('/auth/verify-email', {
      method: 'GET',
      params: { token },
    });
  }

  async resendVerification(email) {
    return this.base.request('/auth/resend-verification', {
      method: 'POST',
      body: { email },
    });
  }
}

